import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Truck, Package, MapPin, Phone, Clock,
  CheckCircle, Navigation, Wind, Home, Bell, BellOff,
  LocateFixed, LocateOff, Check, X, ExternalLink, Map, Camera
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DriverDashboard = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [deliveries, setDeliveries] = useState({ orders: [], rentals: [], driver: null });
  const [activeTab, setActiveTab] = useState('active');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [gpsActive, setGpsActive] = useState(false);
  const gpsIntervalRef = useRef(null);
  const watchIdRef = useRef(null);
  const wakeLockRef = useRef(null);
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error('upload failed');
      const data = await res.json();
      const photoUrl = `${API_URL}${data.url}`;
      const r2 = await fetch(`${API_URL}/api/driver/profile/photo`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_url: photoUrl })
      });
      if (!r2.ok) throw new Error('save failed');
      setDeliveries(prev => ({ ...prev, driver: { ...(prev.driver || {}), photo_url: photoUrl } }));
      toast.success(language === 'pt' ? 'Foto de perfil atualizada!' : 'Profile photo updated!');
    } catch (err) {
      toast.error(language === 'pt' ? 'Erro ao carregar a foto' : 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
      }
    } catch (e) { console.debug('wakeLock failed', e); }
  }, []);

  // GPS Location Broadcasting
  const sendLocation = useCallback(async (lat, lng) => {
    try {
      await fetch(`${API_URL}/api/driver/location`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ latitude: lat, longitude: lng })
      });
    } catch (err) {
      console.error('GPS send error:', err);
    }
  }, [token]);

  const startGPS = useCallback((silent = false) => {
    if (!navigator.geolocation) {
      toast.error(language === 'pt' ? 'GPS não suportado' : 'GPS not supported');
      return;
    }
    setGpsActive(true);
    localStorage.setItem('driver_gps_active', 'true');
    requestWakeLock();
    if (!silent) {
      toast.success(language === 'pt' ? 'GPS ativado - localização a ser transmitida' : 'GPS active - broadcasting location');
    }

    // Avoid duplicate watchers when resuming
    if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);

    // Use watchPosition for continuous tracking. Do NOT stop on errors —
    // transient errors (tunnel, app switch) should not turn tracking off.
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        sendLocation(pos.coords.latitude, pos.coords.longitude);
      },
      (err) => {
        console.warn('GPS watch error (keeping tracking on):', err);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );

    // Also send periodically as backup
    if (gpsIntervalRef.current) clearInterval(gpsIntervalRef.current);
    gpsIntervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => sendLocation(pos.coords.latitude, pos.coords.longitude),
        () => {},
        { enableHighAccuracy: true, maximumAge: 10000, timeout: 8000 }
      );
    }, 8000);
  }, [language, sendLocation, requestWakeLock]);

  const stopGPS = useCallback(() => {
    setGpsActive(false);
    localStorage.removeItem('driver_gps_active');
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (gpsIntervalRef.current) {
      clearInterval(gpsIntervalRef.current);
      gpsIntervalRef.current = null;
    }
    if (wakeLockRef.current) {
      try { wakeLockRef.current.release(); } catch (e) { console.debug('wakeLock release failed', e); }
      wakeLockRef.current = null;
    }
    toast.info(language === 'pt' ? 'GPS desativado' : 'GPS deactivated');
  }, [language]);

  useEffect(() => {
    // Resume GPS automatically if the driver had it on (reload / re-entry).
    if (localStorage.getItem('driver_gps_active') === 'true') {
      startGPS(true);
    }
    // When the driver returns to the page (e.g. after using Maps), re-acquire
    // the wake lock and make sure the watcher is still running.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('driver_gps_active') === 'true') {
        requestWakeLock();
        if (watchIdRef.current === null) startGPS(true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (gpsIntervalRef.current) clearInterval(gpsIntervalRef.current);
      if (wakeLockRef.current) { try { wakeLockRef.current.release(); } catch (e) { console.debug('wakeLock release failed', e); } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
      if (permission === 'granted') {
        toast.success(language === 'pt' ? 'Notificações ativadas!' : 'Notifications enabled!');
      }
    }
  }, [language]);

  const showNotification = useCallback((title, body, orderId) => {
    if (notificationsEnabled && 'Notification' in window) {
      const notification = new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        vibrate: [200, 100, 200],
        tag: orderId,
        requireInteraction: true
      });
      notification.onclick = () => { window.focus(); notification.close(); };
    }
  }, [notificationsEnabled]);

  useEffect(() => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  const fetchDeliveries = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/driver/deliveries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeliveries(data);
      }
    } catch (error) {
      console.error('Error fetching deliveries:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  // WebSocket for real-time updates
  const driverId = deliveries.driver?.driver_id;
  useEffect(() => {
    if (!driverId) return;

    const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/driver_${driverId}`);

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'new_order') {
        fetchDeliveries();
        showNotification(
          language === 'pt' ? 'Novo Pedido!' : 'New Order!',
          data.message || (language === 'pt' ? 'Novo pedido atribuído a si' : 'New order assigned to you'),
          data.order_id
        );
        toast.info(language === 'pt' ? 'Novo pedido atribuído!' : 'New order assigned!');
      } else if (data.type === 'order_update' || data.type === 'notification') {
        fetchDeliveries();
      }
    };

    return () => ws.close();
  }, [driverId, language, showNotification, fetchDeliveries]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/driver/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        setDeliveries(prev => ({
          ...prev,
          orders: prev.orders.map(o =>
            o.order_id === orderId ? { ...o, status } : o
          )
        }));
        toast.success(language === 'pt' ? 'Status atualizado!' : 'Status updated!');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro ao atualizar' : 'Update failed');
    }
  };

  const acceptOrder = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/driver/dispatch/${orderId}/accept`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(language === 'pt' ? 'Pedido aceite!' : 'Order accepted!');
        fetchDeliveries();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro' : 'Error');
    }
  };

  const rejectOrder = async (orderId) => {
    try {
      const response = await fetch(`${API_URL}/api/driver/dispatch/${orderId}/reject`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.info(language === 'pt' ? 'Pedido rejeitado' : 'Order rejected');
        fetchDeliveries();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Error');
      }
    } catch (error) {
      toast.error(language === 'pt' ? 'Erro' : 'Error');
    }
  };

  const statusOptions = [
    { value: 'ready', label: language === 'pt' ? 'Pronto' : 'Ready' },
    { value: 'out_for_delivery', label: language === 'pt' ? 'Em Entrega' : 'Out for Delivery' },
    { value: 'delivered', label: language === 'pt' ? 'Entregue' : 'Delivered' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      received: 'bg-yellow-100 text-yellow-800',
      preparing: 'bg-blue-100 text-blue-800',
      ready: 'bg-purple-100 text-purple-800',
      out_for_delivery: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const pendingAcceptOrders = deliveries.orders.filter(o => o.status === 'received' && o.dispatch_status === 'auto_assigned');
  const activeOrders = deliveries.orders.filter(o => o.status !== 'delivered' && !(o.status === 'received' && o.dispatch_status === 'auto_assigned'));
  const completedOrders = deliveries.orders.filter(o => o.status === 'delivered');
  const activeRentals = (deliveries.rentals || []).filter(r => r.status !== 'delivered' && r.status !== 'cancelled');

  const openInMaps = (order) => {
    const gps = order.gps_coordinates;
    if (gps) {
      // gps can be a string "lat,lng" or an object {lat, lng}
      let lat, lng;
      if (typeof gps === 'string') {
        const parts = gps.split(',');
        lat = parts[0]?.trim();
        lng = parts[1]?.trim();
      } else {
        lat = gps.lat || gps.latitude;
        lng = gps.lng || gps.longitude;
      }
      if (lat && lng) {
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
        return;
      }
    }
    // Fallback to address
    if (order.delivery_location) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.delivery_location)}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] p-8">
        <Skeleton className="h-12 w-48 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <div className="bg-[#FF6B35] text-white py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 rounded-full" data-testid="back-home-btn">
                <Home size={20} />
              </Button>
            </Link>
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              className="relative shrink-0"
              title={language === 'pt' ? 'Alterar foto de perfil' : 'Change profile photo'}
              data-testid="driver-photo-btn"
            >
              {deliveries.driver?.photo_url ? (
                <img src={deliveries.driver.photo_url} alt={user?.name} className={`w-11 h-11 rounded-full object-cover border-2 border-white/70 ${uploadingPhoto ? 'opacity-50' : ''}`} />
              ) : (
                <div className={`w-11 h-11 rounded-full bg-white/20 border-2 border-white/70 flex items-center justify-center text-white font-bold ${uploadingPhoto ? 'opacity-50' : ''}`}>
                  {user?.name?.charAt(0) || 'D'}
                </div>
              )}
              <span className="absolute -bottom-1 -right-1 bg-white text-[#FF6B35] rounded-full p-0.5 shadow">
                <Camera size={12} />
              </span>
            </button>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoUpload}
              data-testid="driver-photo-input"
            />
            <div>
              <h1 className="text-xl font-bold" data-testid="driver-title">
                {language === 'pt' ? 'Painel Entregador' : 'Driver Dashboard'}
              </h1>
              <p className="text-white/80 text-sm">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={gpsActive ? stopGPS : startGPS}
              className={`text-white hover:bg-white/10 rounded-full ${gpsActive ? 'bg-white/20' : ''}`}
              data-testid="gps-toggle-btn"
            >
              {gpsActive ? <LocateFixed size={20} className="animate-pulse" /> : <LocateOff size={20} />}
              <span className="ml-1 text-xs hidden sm:inline">{gpsActive ? 'GPS ON' : 'GPS OFF'}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={requestNotificationPermission}
              className={`text-white hover:bg-white/10 rounded-full ${notificationsEnabled ? '' : 'animate-pulse'}`}
              data-testid="notification-toggle"
            >
              {notificationsEnabled ? <Bell size={20} /> : <BellOff size={20} />}
            </Button>
            {deliveries.driver && (
              <Badge className={deliveries.driver.is_available ? 'bg-white text-green-600' : 'bg-white/20 text-white'}>
                {deliveries.driver.is_available
                  ? (language === 'pt' ? 'Disponivel' : 'Available')
                  : (language === 'pt' ? 'Ocupado' : 'Busy')}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-8">
        {/* Pending Accept Orders */}
        {pendingAcceptOrders.length > 0 && (
          <div className="mb-6 space-y-4">
            <h2 className="text-base font-bold text-[#004E64] flex items-center gap-2">
              <Bell className="text-[#FF6B35] animate-bounce" size={20} />
              {language === 'pt' ? 'Novos Pedidos - Aceitar ou Rejeitar' : 'New Orders - Accept or Reject'}
            </h2>
            {pendingAcceptOrders.map(order => (
              <Card key={order.order_id} className="rounded-2xl border-2 border-[#FF6B35] bg-[#FF6B35]/5 animate-pulse-slow" data-testid={`pending-order-${order.order_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-bold text-[#004E64] text-lg">#{order.order_id.slice(-8)}</span>
                    <Badge className="bg-[#FF6B35] text-white">
                      {language === 'pt' ? 'Novo' : 'New'}
                    </Badge>
                  </div>

                  {/* Order Items */}
                  <div className="bg-white rounded-xl p-3 mb-3 border border-[#004E64]/10">
                    <p className="text-xs text-[#9FA2B4] mb-2 font-semibold">{language === 'pt' ? 'Itens do Pedido' : 'Order Items'}</p>
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm py-1 border-b border-[#004E64]/5 last:border-0">
                        <span className="text-[#004E64]">{item.quantity}x {item.name}</span>
                        <span className="font-semibold text-[#004E64]">{'\u20AC'}{(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  {/* Restaurant (Pickup) + Customer + Delivery */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    {order.restaurant_name && (
                      <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-xl p-3">
                        <p className="text-xs text-[#9FA2B4] mb-1">{language === 'pt' ? 'Recolha' : 'Pickup'}</p>
                        <p className="text-sm font-bold text-[#FF6B35]">{order.restaurant_name}</p>
                        {order.restaurant_address && (
                          <p className="text-xs text-[#004E64] mt-1">{order.restaurant_address}</p>
                        )}
                      </div>
                    )}
                    <div className="bg-[#F4F1EA] rounded-xl p-3">
                      <p className="text-xs text-[#9FA2B4] mb-1">{language === 'pt' ? 'Cliente' : 'Customer'}</p>
                      <p className="text-sm font-semibold text-[#004E64]">{order.customer_name}</p>
                      <a href={`tel:${order.phone}`} className="text-sm text-[#2EC4B6] flex items-center gap-1 mt-1 hover:underline">
                        <Phone size={12} /> {order.phone}
                      </a>
                    </div>
                    <div className="bg-[#F4F1EA] rounded-xl p-3">
                      <p className="text-xs text-[#9FA2B4] mb-1">{language === 'pt' ? 'Entrega' : 'Delivery'}</p>
                      <p className="text-sm font-semibold text-[#004E64] flex items-center gap-1">
                        <MapPin size={12} /> {order.delivery_location}
                      </p>
                      {order.hotel_name && (
                        <p className="text-xs text-[#9FA2B4] mt-1">{order.hotel_name} {order.room_number && `- ${language === 'pt' ? 'Quarto' : 'Room'} ${order.room_number}`}</p>
                      )}
                    </div>
                  </div>

                  {/* Navigation Button */}
                  <Button
                    onClick={() => openInMaps(order)}
                    className="w-full bg-[#004E64] hover:bg-[#003A4A] rounded-xl mb-3"
                    data-testid={`navigate-order-${order.order_id}`}
                  >
                    <Map size={16} className="mr-2" />
                    {language === 'pt' ? 'Ver no Mapa / Navegar' : 'View on Map / Navigate'}
                    <ExternalLink size={14} className="ml-2" />
                  </Button>

                  {order.notes && (
                    <div className="p-2 bg-yellow-50 rounded-xl mb-3">
                      <p className="text-xs text-yellow-800"><strong>{language === 'pt' ? 'Notas' : 'Notes'}:</strong> {order.notes}</p>
                    </div>
                  )}

                  {/* Accept/Reject Buttons */}
                  <div className="flex gap-3">
                    <Button
                      onClick={() => acceptOrder(order.order_id)}
                      className="flex-1 bg-[#2EC4B6] hover:bg-[#25A89D] rounded-full"
                      data-testid={`accept-order-${order.order_id}`}
                    >
                      <Check size={18} className="mr-1" />
                      {language === 'pt' ? 'Aceitar' : 'Accept'}
                    </Button>
                    <Button
                      onClick={() => rejectOrder(order.order_id)}
                      variant="outline"
                      className="flex-1 rounded-full border-red-400 text-red-500 hover:bg-red-50"
                      data-testid={`reject-order-${order.order_id}`}
                    >
                      <X size={18} className="mr-1" />
                      {language === 'pt' ? 'Rejeitar' : 'Reject'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Entregas Ativas' : 'Active Deliveries'}</p>
                  <p className="text-3xl font-bold text-[#004E64]" data-testid="active-count">{activeOrders.length}</p>
                </div>
                <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                  <Truck className="text-[#FF6B35]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Concluidas Hoje' : 'Completed Today'}</p>
                  <p className="text-3xl font-bold text-[#004E64]" data-testid="completed-count">{completedOrders.length}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#9FA2B4] text-sm">{language === 'pt' ? 'Total Entregas' : 'Total Deliveries'}</p>
                  <p className="text-3xl font-bold text-[#004E64]" data-testid="total-count">{deliveries.driver?.total_deliveries || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#2EC4B6]/10 rounded-full flex items-center justify-center">
                  <Package className="text-[#2EC4B6]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Deliveries Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white rounded-full p-1 shadow-sm mb-6">
            <TabsTrigger value="active" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white">
              <Navigation size={18} className="mr-2" />
              {language === 'pt' ? 'Ativas' : 'Active'} ({activeOrders.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white">
              <CheckCircle size={18} className="mr-2" />
              {language === 'pt' ? 'Concluidas' : 'Completed'} ({completedOrders.length})
            </TabsTrigger>
            <TabsTrigger value="shishas" className="rounded-full data-[state=active]:bg-[#FF6B35] data-[state=active]:text-white" data-testid="driver-tab-shishas">
              <Wind size={18} className="mr-2" />
              {language === 'pt' ? 'Shishas' : 'Shishas'} ({activeRentals.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {activeOrders.length === 0 ? (
              <Card className="rounded-2xl">
                <CardContent className="p-12 text-center">
                  <Truck size={48} className="mx-auto text-[#9FA2B4] mb-4" />
                  <p className="text-[#9FA2B4]">
                    {language === 'pt' ? 'Nenhuma entrega ativa' : 'No active deliveries'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {activeOrders.map(order => (
                  <Card key={order.order_id} className="rounded-2xl border-l-4 border-l-[#FF6B35]">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="font-bold text-[#004E64] text-lg">#{order.order_id.slice(-8)}</span>
                        <Badge className={getStatusColor(order.status)}>
                          {statusOptions.find(s => s.value === order.status)?.label || order.status}
                        </Badge>
                        {order.is_beach_mode && (
                          <Badge className="bg-[#2EC4B6] text-white">
                            <Wind size={14} className="mr-1" /> Beach
                          </Badge>
                        )}
                      </div>

                      {/* Order Items with Prices */}
                      <div className="bg-white border border-[#004E64]/10 rounded-xl p-4 mb-4">
                        <p className="text-xs text-[#9FA2B4] mb-2 font-semibold">{language === 'pt' ? 'Itens do Pedido' : 'Order Items'}</p>
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm py-1.5 border-b border-[#004E64]/5 last:border-0">
                            <span className="text-[#004E64]">{item.quantity}x {item.name}</span>
                            <span className="font-semibold text-[#004E64]">{'\u20AC'}{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>

                      {/* Restaurant (Pickup) + Customer & Delivery Info */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        {order.restaurant_name && (
                          <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 rounded-xl p-4">
                            <p className="text-xs text-[#9FA2B4] mb-1">{language === 'pt' ? 'Recolha no Restaurante' : 'Pickup at Restaurant'}</p>
                            <p className="font-bold text-[#FF6B35]">{order.restaurant_name}</p>
                            {order.restaurant_address && (
                              <p className="text-xs text-[#004E64] mt-1">{order.restaurant_address}</p>
                            )}
                          </div>
                        )}
                        <div className="bg-[#F4F1EA] rounded-xl p-4">
                          <p className="text-xs text-[#9FA2B4] mb-1">{language === 'pt' ? 'Cliente' : 'Customer'}</p>
                          <p className="font-semibold text-[#004E64]">{order.customer_name}</p>
                          <a href={`tel:${order.phone}`} className="text-sm text-[#2EC4B6] flex items-center gap-1 mt-1 hover:underline">
                            <Phone size={14} /> {order.phone}
                          </a>
                        </div>
                        <div className="bg-[#F4F1EA] rounded-xl p-4">
                          <p className="text-xs text-[#9FA2B4] mb-1">{language === 'pt' ? 'Entrega' : 'Delivery'}</p>
                          <p className="font-semibold text-[#004E64] flex items-center gap-1">
                            <MapPin size={14} /> {order.delivery_location}
                          </p>
                          {order.hotel_name && (
                            <p className="text-xs text-[#9FA2B4] mt-1">
                              {order.hotel_name} {order.room_number && `- ${language === 'pt' ? 'Quarto' : 'Room'} ${order.room_number}`}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Navigation Button */}
                      <Button
                        onClick={() => openInMaps(order)}
                        className="w-full bg-[#004E64] hover:bg-[#003A4A] rounded-xl mb-4"
                        data-testid={`navigate-active-${order.order_id}`}
                      >
                        <Map size={16} className="mr-2" />
                        {language === 'pt' ? 'Ver no Mapa / Navegar' : 'View on Map / Navigate'}
                        <ExternalLink size={14} className="ml-2" />
                      </Button>

                      {order.notes && (
                        <div className="p-3 bg-yellow-50 rounded-xl mb-4">
                          <p className="text-sm text-yellow-800">
                            <strong>{language === 'pt' ? 'Notas' : 'Notes'}:</strong> {order.notes}
                          </p>
                        </div>
                      )}

                      {/* Status Controls */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Select
                          value={order.status}
                          onValueChange={(value) => updateOrderStatus(order.order_id, value)}
                        >
                          <SelectTrigger className="rounded-xl flex-1" data-testid={`status-select-${order.order_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map(status => (
                              <SelectItem key={status.value} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Button
                          onClick={() => updateOrderStatus(order.order_id, 'delivered')}
                          className="bg-[#2EC4B6] hover:bg-[#25A89D] rounded-full"
                          data-testid={`deliver-btn-${order.order_id}`}
                        >
                          <CheckCircle size={18} className="mr-2" />
                          {language === 'pt' ? 'Marcar Entregue' : 'Mark Delivered'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="completed">
            {completedOrders.length === 0 ? (
              <Card className="rounded-2xl">
                <CardContent className="p-12 text-center">
                  <CheckCircle size={48} className="mx-auto text-[#9FA2B4] mb-4" />
                  <p className="text-[#9FA2B4]">
                    {language === 'pt' ? 'Nenhuma entrega concluida hoje' : 'No completed deliveries today'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {completedOrders.map(order => (
                  <Card key={order.order_id} className="rounded-2xl border-l-4 border-l-green-500">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <span className="font-bold text-[#004E64]">#{order.order_id.slice(-8)}</span>
                            <Badge className="bg-green-100 text-green-800">
                              {language === 'pt' ? 'Entregue' : 'Delivered'}
                            </Badge>
                          </div>
                          <p className="text-sm text-[#9FA2B4]">
                            {order.customer_name} - {order.delivery_location}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-[#FF6B35]">{'\u20AC'}{order.total?.toFixed(2)}</p>
                          <p className="text-xs text-[#9FA2B4] flex items-center gap-1">
                            <Clock size={12} />
                            {order.updated_at ? new Date(order.updated_at).toLocaleTimeString() : ''}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Shisha rentals tab — same UX parity as food orders */}
          <TabsContent value="shishas">
            {activeRentals.length === 0 ? (
              <Card className="rounded-2xl">
                <CardContent className="p-12 text-center">
                  <Wind size={48} className="mx-auto text-[#9FA2B4] mb-4" />
                  <p className="text-[#9FA2B4]">{language === 'pt' ? 'Sem alugueres de shisha ativos' : 'No active shisha rentals'}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="driver-shisha-list">
                {activeRentals.map((rental) => {
                  const next = rental.status === 'received' ? 'preparing'
                    : rental.status === 'preparing' ? 'ready'
                    : rental.status === 'ready' ? 'out_for_delivery'
                    : rental.status === 'out_for_delivery' ? 'delivered' : null;
                  const advanceLabel = next === 'preparing' ? (language === 'pt' ? 'Aceitar' : 'Accept')
                    : next === 'ready' ? (language === 'pt' ? 'Pronto' : 'Ready')
                    : next === 'out_for_delivery' ? (language === 'pt' ? 'Sai para entrega' : 'Out for delivery')
                    : next === 'delivered' ? (language === 'pt' ? 'Entregue' : 'Delivered') : null;
                  return (
                    <Card key={rental.rental_id} className="rounded-2xl border-l-4 border-l-purple-500" data-testid={`driver-rental-${rental.rental_id}`}>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4 flex-wrap">
                          <span className="font-bold text-[#004E64] text-lg">#{rental.rental_id.slice(-8)}</span>
                          <Badge className="bg-purple-100 text-purple-800"><Wind size={12} className="mr-1" /> Shisha</Badge>
                          <Badge className={getStatusColor(rental.status)}>{rental.status}</Badge>
                          {rental.delivery_time && (
                            <Badge variant="outline"><Clock size={12} className="mr-1" />{rental.delivery_time}</Badge>
                          )}
                        </div>

                        <div className="bg-white border border-[#004E64]/10 rounded-xl p-4 mb-4">
                          <p className="text-xs text-[#9FA2B4] mb-2 font-semibold">{language === 'pt' ? 'Detalhes do aluguer' : 'Rental details'}</p>
                          <p className="text-sm text-[#004E64]">
                            <strong>{rental.shisha_name}</strong> · {rental.flavor_name} · {rental.duration_hours}h
                          </p>
                          {(rental.extra_charcoal || rental.extra_hose || (rental.selected_extras?.length > 0)) && (
                            <p className="text-xs text-[#9FA2B4] mt-1">
                              {rental.extra_charcoal && (language === 'pt' ? '+ Carvão extra ' : '+ Extra charcoal ')}
                              {rental.extra_hose && (language === 'pt' ? '+ Mangueira extra ' : '+ Extra hose ')}
                              {rental.selected_extras?.map(e => `+ ${e.quantity}× ${e.name}`).join(' ')}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                          <div className="bg-[#F4F1EA] rounded-xl p-4">
                            <p className="text-xs text-[#9FA2B4] mb-1">{language === 'pt' ? 'Cliente' : 'Customer'}</p>
                            <p className="font-semibold text-[#004E64]">{rental.customer_name}</p>
                            <a href={`tel:${rental.phone}`} className="text-sm text-[#2EC4B6] flex items-center gap-1 mt-1 hover:underline">
                              <Phone size={14} /> {rental.phone}
                            </a>
                          </div>
                          <div className="bg-[#F4F1EA] rounded-xl p-4">
                            <p className="text-xs text-[#9FA2B4] mb-1">{language === 'pt' ? 'Entrega' : 'Delivery'}</p>
                            <p className="font-semibold text-[#004E64] flex items-center gap-1">
                              <MapPin size={14} /> {rental.delivery_location}
                            </p>
                            {rental.gps_coordinates && (
                              <p className="text-xs text-[#2EC4B6] mt-1">📍 {rental.gps_coordinates}</p>
                            )}
                          </div>
                        </div>

                        <Button
                          onClick={() => openInMaps(rental)}
                          className="w-full bg-[#004E64] hover:bg-[#003A4A] rounded-xl mb-4"
                          data-testid={`navigate-rental-${rental.rental_id}`}
                        >
                          <Map size={16} className="mr-2" />
                          {language === 'pt' ? 'Ver no Mapa / Navegar' : 'View on Map / Navigate'}
                          <ExternalLink size={14} className="ml-2" />
                        </Button>

                        {rental.notes && (
                          <div className="p-3 bg-yellow-50 rounded-xl mb-4">
                            <p className="text-sm text-yellow-800">📝 {rental.notes}</p>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <p className="font-bold text-[#FF6B35] text-lg">€{Number(rental.total || 0).toFixed(2)}</p>
                          {advanceLabel && next && (
                            <Button
                              onClick={async () => {
                                const r = await fetch(`${API_URL}/api/admin/shisha-rentals/${rental.rental_id}/status`, {
                                  method: 'PUT',
                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ status: next }),
                                });
                                if (r.ok) {
                                  toast.success(language === 'pt' ? 'Atualizado' : 'Updated');
                                  setDeliveries(prev => ({
                                    ...prev,
                                    rentals: prev.rentals.map(x => x.rental_id === rental.rental_id ? { ...x, status: next } : x),
                                  }));
                                } else {
                                  toast.error(language === 'pt' ? 'Erro' : 'Failed');
                                }
                              }}
                              className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                              data-testid={`advance-rental-${rental.rental_id}`}
                            >
                              {advanceLabel}
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DriverDashboard;
